import { useState, useEffect, useMemo } from 'react'
import { useTheme } from '../../contexts/ThemeContext'
import { supabase } from '../../lib/supabase'
import { usePresupuestoContext } from '../../contexts/PresupuestoContext'
import { CATEGORIAS_GASTO } from '../../lib/categorias'

const C = 314.16 // 2π × 50, circunferencia del donut (r=50)

function catColor(t) {
  return {
    Hogar:        t.sky,
    Comida:       t.gold,
    Bienestar:    t.sage,
    Compras:      t.mauve,
    Transporte:   t.lav,
    Aprendizaje:  t.terra,
    Experiencias: t.blush,
    Viajes:       t.goldPale,
    Amor:         t.sagePale,
    Otro:         t.inkLight,
  }
}

const fmt = (n) => Math.round(n).toLocaleString('es')

export default function Presupuesto() {
  const { theme } = useTheme()
  const s = makeStyles(theme)

  const { limites, isInherited, loading: loadingL, saving, loadMes, guardarLimite, eliminarLimite } = usePresupuestoContext()
  const colors = useMemo(() => catColor(theme), [theme])

  const [mesOffset,   setMesOffset]   = useState(0)
  const [gastos,      setGastos]      = useState([])
  const [loadingG,    setLoadingG]    = useState(true)
  const [sheetCat,    setSheetCat]    = useState(null)
  const [inputLimite, setInputLimite] = useState('')
  const [tempLimites, setTempLimites] = useState({})

  const ahora  = new Date()
  const mesRef = useMemo(
    () => new Date(ahora.getFullYear(), ahora.getMonth() + mesOffset, 1),
    [mesOffset] // ahora is stable (computed at render from Date())
  )
  const mesLabel      = mesRef.toLocaleDateString('es', { month: 'long', year: 'numeric' })
  const diasMes       = new Date(mesRef.getFullYear(), mesRef.getMonth() + 1, 0).getDate()
  const diaHoy        = mesOffset === 0 ? ahora.getDate() : mesOffset > 0 ? 0 : diasMes
  const diasRestantes = Math.max(0, diasMes - diaHoy)

  // Cargar presupuesto del mes cuando cambia mesOffset
  useEffect(() => {
    loadMes(mesRef.getFullYear(), mesRef.getMonth() + 1)
  }, [mesOffset])
  // Cargar gastos del mes
  useEffect(() => {
    async function fetchMes() {
      setLoadingG(true)
      const inicio = new Date(mesRef.getFullYear(), mesRef.getMonth(), 1)
      const fin    = new Date(mesRef.getFullYear(), mesRef.getMonth() + 1, 0, 23, 59, 59)
      const { data } = await supabase
        .from('gastos')
        .select('*')
        .gte('fecha', inicio.toISOString())
        .lte('fecha', fin.toISOString())
        .order('fecha', { ascending: false })
      setGastos(data || [])
      setLoadingG(false)
    }
    fetchMes()
  }, [mesOffset])
  // ── Cálculos de gastos reales ──
  const soloGastos = useMemo(() => gastos.filter(g => g.tipo === 'gasto'), [gastos])

  const porCategoria = useMemo(
    () => soloGastos.reduce((acc, g) => ({
      ...acc, [g.categoria]: (acc[g.categoria] || 0) + g.monto
    }), {}),
    [soloGastos]
  )

  const ingresoReal = useMemo(
    () => gastos.filter(g => g.tipo === 'ingreso').reduce((a, g) => a + g.monto, 0),
    [gastos]
  )

  const ahorroReal = useMemo(
    () => gastos.filter(g => g.tipo === 'ahorro').reduce((a, g) => a + g.monto, 0),
    [gastos]
  )

  // ── Cálculos de presupuesto ──
  const ingresoPlanificado = limites['__ingreso__'] || 0
  const ahorroPlanificado  = limites['__ahorro__']  || 0

  const totalGastado = useMemo(
    () => Object.values(porCategoria).reduce((a, b) => a + b, 0),
    [porCategoria]
  )

  const totalGastoPlan = useMemo(
    () => Object.entries(limites)
      .filter(([cat]) => !cat.startsWith('__'))
      .reduce((a, [, v]) => a + v, 0),
    [limites]
  )

  // Alias para el donut/hero (solo gastos planificados, sin ingresos/ahorro especiales)
  const totalPresupuesto = totalGastoPlan
  const pctUsado         = totalPresupuesto > 0 ? Math.min(100, Math.round((totalGastado / totalPresupuesto) * 100)) : 0
  const disponible       = totalPresupuesto - totalGastado

  // Distribución del sobre de ingresos
  const porDistribuir = ingresoPlanificado - totalGastoPlan - ahorroPlanificado

  // ── Segmentos del donut ──
  const segmentos = useMemo(() => {
    let acum = 0
    return Object.entries(porCategoria)
      .filter(([, monto]) => monto > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([cat, monto]) => {
        const pct  = totalGastado > 0 ? monto / totalGastado : 0
        const dash = pct * C
        const rot  = acum * 360 - 90
        acum += pct
        return { cat, monto, pct, dash, gap: C - dash, rot, color: colors[cat] || theme.inkLight }
      })
      .filter(seg => seg.dash > 0.5)
  }, [porCategoria, totalGastado, colors, theme.inkLight])

  const topCats = segmentos.slice(0, 4)

  // ── Insights de presupuesto (hasta 2) ──
  const budgetInsights = useMemo(() => {
    const insights = []

    // 0. Sobre de ingresos: sobrepasado
    if (ingresoPlanificado > 0 && porDistribuir < 0) {
      insights.push({
        text: `te pasas $${fmt(Math.abs(porDistribuir))} del ingreso planificado. ¿qué ajustarías?`,
        type: 'warning',
      })
    }

    // 0.5. Sobre de ingresos: mucho sin asignar (>30%)
    if (ingresoPlanificado > 0 && porDistribuir > ingresoPlanificado * 0.3) {
      insights.push({
        text: `te quedan $${fmt(porDistribuir)} sin asignar. ¿los sumas al ahorro?`,
        type: 'neutral',
      })
    }

    // 1. Categorías sobre el límite
    const overBudget = Object.entries(limites).filter(([cat, lim]) =>
      !cat.startsWith('__') && lim > 0 && (porCategoria[cat] || 0) > lim
    )
    if (overBudget.length > 0) {
      const names = overBudget.map(([c]) => c).join(', ')
      insights.push({ text: `superaste el límite en ${names}. ¿fue algo puntual?`, type: 'warning' })
    }

    // 2. Progreso de ahorro
    if (ahorroReal > 0 && ahorroPlanificado > 0) {
      const pctAhorro = Math.round((ahorroReal / ahorroPlanificado) * 100)
      insights.push({
        text: `llevas $${fmt(ahorroReal)} de $${fmt(ahorroPlanificado)} en ahorro este mes. vas al ${pctAhorro}%.`,
        type: pctAhorro >= 80 ? 'positive' : 'neutral',
      })
    }

    // 3. Categoría al 80%+ del límite con días restantes
    if (diasRestantes > 3) {
      const approaching = Object.entries(limites).find(([cat, lim]) => {
        if (cat.startsWith('__')) return false
        const spent = porCategoria[cat] || 0
        return lim > 0 && spent / lim >= 0.8 && spent < lim
      })
      if (approaching) {
        const [cat, lim] = approaching
        const pct = Math.round(((porCategoria[cat] || 0) / lim) * 100)
        insights.push({
          text: `${cat} al ${pct}% del límite. quedan ${diasRestantes} días.`,
          type: 'neutral',
        })
      }
    }

    // 4. Ritmo diario vs ideal
    if (totalPresupuesto > 0 && diaHoy > 0 && insights.length < 2) {
      const gastoHoy    = totalGastado / diaHoy
      const idealDiario = totalPresupuesto / diasMes
      if (gastoHoy > idealDiario * 1.2) {
        insights.push({
          text: `gastas $${fmt(gastoHoy)}/día. el ideal para cerrar bien es $${fmt(idealDiario)}.`,
          type: 'warning',
        })
      } else if (gastoHoy <= idealDiario * 0.9) {
        insights.push({
          text: `$${fmt(gastoHoy)}/día frente al ideal de $${fmt(idealDiario)}. vas dentro del margen.`,
          type: 'positive',
        })
      }
    }

    // 5. Cierre de mes (últimos 5 días) con presupuesto a favor
    if (diasRestantes > 0 && diasRestantes <= 5 && totalPresupuesto > 0 && disponible > 0) {
      insights.push({
        text: `quedan ${diasRestantes} día${diasRestantes > 1 ? 's' : ''}. tienes $${fmt(disponible)} disponible. buen cierre.`,
        type: 'positive',
      })
    }

    // 6. Sin límites configurados
    if (totalPresupuesto === 0 && ingresoPlanificado === 0) {
      insights.push({
        text: 'agrega tu ingreso o límites por categoría para ver cómo distribuyes tu mes.',
        type: 'neutral',
      })
    }

    // 7. Fallback positivo
    if (insights.length === 0) {
      insights.push({
        text: `llevas $${fmt(totalGastado)} de $${fmt(totalPresupuesto)} este mes.`,
        type: 'positive',
      })
    }

    return insights.slice(0, 2)
  }, [
    limites, porCategoria, totalGastado, totalPresupuesto, disponible,
    ingresoPlanificado, ahorroPlanificado, ahorroReal, porDistribuir,
    diaHoy, diasMes, diasRestantes,
  ])

  function openCatSheet(cat) {
    setSheetCat(cat)
    setInputLimite(limites[cat] != null ? String(limites[cat]) : '')
  }

  function openAllSheet() {
    const gastoCats = Object.fromEntries(
      Object.entries(limites).filter(([cat]) => !cat.startsWith('__'))
    )
    setTempLimites(gastoCats)
    setSheetCat('__all__')
  }

  function closeSheet() {
    setSheetCat(null)
    setInputLimite('')
  }

  function insightCardStyle(type) {
    if (type === 'warning')  return { ...s.insightCard, background: theme.blush + '44', borderColor: theme.terra + '44' }
    if (type === 'positive') return { ...s.insightCard, background: theme.sagePale + '55', borderColor: theme.sage + '55' }
    return s.insightCard
  }

  if (loadingG || loadingL) return <div style={s.loading}>cargando tu presupuesto</div>

  return (
    <div style={s.container}>

      {/* ── Header ── */}
      <p style={s.eyebrow}>presupuesto mensual</p>
      <div style={s.mesNav}>
        <button style={s.navBtn} onClick={() => setMesOffset(o => o - 1)}>‹</button>
        <h1 style={s.titulo}>{mesLabel}</h1>
        <button
          style={{ ...s.navBtn, opacity: mesOffset >= 3 ? 0.2 : 1 }}
          disabled={mesOffset >= 3}
          onClick={() => setMesOffset(o => o + 1)}
        >›</button>
      </div>

      {/* ── Banner heredado ── */}
      {isInherited && (
        <div style={s.inheritedBanner}>
          <p style={s.inheritedText}>basado en el mes anterior — edita con libertad</p>
        </div>
      )}

      {/* ── Sección Ingresos ── */}
      <div style={s.secHeader}>
        <h2 style={s.secTitulo}>
          <em style={{ color: theme.sage, fontStyle: 'italic' }}>ingresos</em>
        </h2>
      </div>
      <div style={s.ingresoCard} onClick={() => openCatSheet('__ingreso__')}>
        <div style={s.ingresoLeft}>
          <p style={s.ingresoEye}>ingreso planificado</p>
          <p style={s.ingresoBig}>
            {ingresoPlanificado > 0 ? `$${fmt(ingresoPlanificado)}` : 'sin definir'}
          </p>
          {ingresoReal > 0 && (
            <p style={s.ingresoSub}>ingresado este mes: <strong>${fmt(ingresoReal)}</strong></p>
          )}
        </div>
        <div style={s.ingresoEdit}>
          <span style={s.editChip}>editar ✦</span>
        </div>
      </div>

      {/* ── Distribución del sobre (solo si hay ingreso planificado) ── */}
      {ingresoPlanificado > 0 && (
        <div style={s.sobreCard}>
          <p style={s.sobreTitle}>cómo distribuyes tus ingresos</p>
          <div style={s.sobreRow}>
            <span style={s.sobreLabel}>gastos planificados</span>
            <span style={s.sobreVal}>${fmt(totalGastoPlan)}</span>
          </div>
          {ahorroPlanificado > 0 && (
            <div style={s.sobreRow}>
              <span style={s.sobreLabel}>ahorro planificado</span>
              <span style={s.sobreVal}>${fmt(ahorroPlanificado)}</span>
            </div>
          )}
          <div style={s.sobreDivider} />
          <div style={s.sobreRow}>
            <span style={{
              ...s.sobreLabel,
              fontWeight: 600,
              color: porDistribuir < 0 ? theme.terra : theme.sage,
            }}>
              {porDistribuir >= 0 ? 'por distribuir' : 'te pasas por'}
            </span>
            <span style={{
              ...s.sobreVal,
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 20,
              fontWeight: 700,
              color: porDistribuir < 0 ? theme.terra : theme.sage,
            }}>
              ${fmt(Math.abs(porDistribuir))}
            </span>
          </div>
        </div>
      )}

      {/* ── Hero donut ── */}
      <div style={s.heroCard}>
        <div style={s.donutWrap}>
          <svg width="150" height="150" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" fill="none" stroke={theme.blush + '33'} strokeWidth="14" />
            {segmentos.map((seg, i) => (
              <circle
                key={i}
                cx="60" cy="60" r="50"
                fill="none"
                stroke={seg.color}
                strokeWidth="14"
                strokeDasharray={`${seg.dash} ${seg.gap}`}
                transform={`rotate(${seg.rot} 60 60)`}
              />
            ))}
          </svg>
          <div style={s.donutCenter}>
            {totalPresupuesto > 0 ? (
              <>
                <span style={s.donutPct}>{pctUsado}%</span>
                <span style={s.donutCenterLabel}>usado</span>
              </>
            ) : (
              <>
                <span style={s.donutPct}>—</span>
                <span style={s.donutCenterLabel}>sin límites</span>
              </>
            )}
          </div>
        </div>

        <div style={s.donutSide}>
          <p style={s.donutEye}>disponible en gastos</p>
          <p style={s.donutBig}>
            {totalPresupuesto > 0 ? `$${fmt(disponible)}` : '—'}
          </p>
          <p style={s.donutSm}>
            {totalPresupuesto > 0
              ? `de $${fmt(totalPresupuesto)} total`
              : 'agrega límites para verlo'}
          </p>
          {topCats.length > 0 && (
            <div style={s.chipsRow}>
              {topCats.map(seg => (
                <div key={seg.cat} style={{ ...s.chip, borderColor: seg.color + '99', color: seg.color }}>
                  <div style={{ ...s.chipDot, background: seg.color }} />
                  {seg.cat}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Insights (hasta 2) ── */}
      {budgetInsights.map((insight, i) => (
        <div key={i} style={insightCardStyle(insight.type)}>
          <div style={{
            ...s.insightIc,
            background: insight.type === 'warning' ? theme.terra + '22' : theme.sage + '22',
            color: insight.type === 'warning' ? theme.terraDeep : theme.sage,
          }}>
            {insight.type === 'warning' ? '!' : '✦'}
          </div>
          <p style={s.insightText}>{insight.text}</p>
        </div>
      ))}

      {/* ── Categorías de gasto ── */}
      <div style={s.secHeader}>
        <h2 style={s.secTitulo}>por <em style={{ color: theme.accent, fontStyle: 'italic' }}>categoría</em></h2>
        <button style={s.editarBtn} onClick={openAllSheet}>editar ✦</button>
      </div>

      {CATEGORIAS_GASTO.map(({ icon: Icon, label }) => {
        const gastado    = porCategoria[label] || 0
        const limite     = limites[label]
        const hasLimit   = limite != null && limite > 0
        const overBudget = hasLimit && gastado > limite
        const pct        = hasLimit ? Math.min(100, Math.round((gastado / limite) * 100)) : 0
        const color      = colors[label] || theme.inkLight

        return (
          <div
            key={label}
            style={{ ...s.catCard, ...(overBudget ? s.catCardWarn : {}) }}
            onClick={() => openCatSheet(label)}
          >
            <div style={{ ...s.catIconWrap, background: color + '22' }}>
              <Icon size={20} color={color} />
            </div>
            <div style={s.catInfo}>
              <div style={s.catTop}>
                <span style={s.catNombre}>{label}</span>
                <span style={{ ...s.catMonto, color: overBudget ? theme.terra : theme.ink }}>
                  ${fmt(gastado)}{hasLimit && ` / $${fmt(limite)}`}
                </span>
              </div>
              {hasLimit ? (
                <>
                  <div style={s.barTrack}>
                    <div style={{
                      ...s.barFill,
                      width: `${pct}%`,
                      background: overBudget
                        ? `linear-gradient(90deg, ${theme.terraDeep}, ${theme.terra})`
                        : `linear-gradient(90deg, ${color}88, ${color})`,
                    }} />
                  </div>
                  <div style={s.catBottom}>
                    <span style={{ ...s.catPct, color: overBudget ? theme.terra : theme.inkLight }}>
                      {pct}%
                    </span>
                    {!overBudget && hasLimit && (
                      <span style={s.catLibre}>${fmt(limite - gastado)} libre</span>
                    )}
                  </div>
                </>
              ) : (
                <p style={s.sinLimite}>
                  sin límite — <span style={{ color: theme.accent }}>agregar límite</span>
                </p>
              )}
            </div>
          </div>
        )
      })}


      {/* ── Sección Ahorro ── */}
      <div style={{ ...s.secHeader, marginTop: 24 }}>
        <h2 style={s.secTitulo}>
          <em style={{ color: theme.gold, fontStyle: 'italic' }}>ahorro</em>
        </h2>
      </div>
      <div style={s.ahorroCard} onClick={() => openCatSheet('__ahorro__')}>
        <div style={s.ahorroTop}>
          <div>
            <p style={s.ahorroEye}>meta de ahorro mensual</p>
            <p style={s.ahorroBig}>
              {ahorroPlanificado > 0 ? `$${fmt(ahorroPlanificado)}` : 'sin definir'}
            </p>
          </div>
          <span style={s.editChip}>editar ✦</span>
        </div>
        {ahorroPlanificado > 0 && (
          <>
            <p style={s.ahorroSub}>
              ahorrado este mes: <strong style={{ color: theme.gold }}>${fmt(ahorroReal)}</strong>
            </p>
            <div style={s.barTrack}>
              <div style={{
                ...s.barFill,
                width: `${Math.min(100, Math.round((ahorroReal / ahorroPlanificado) * 100))}%`,
                background: `linear-gradient(90deg, ${theme.gold}88, ${theme.gold})`,
              }} />
            </div>
            <p style={s.ahorroPct}>
              {Math.min(100, Math.round((ahorroReal / ahorroPlanificado) * 100))}% de la meta
            </p>
          </>
        )}
        {ahorroPlanificado === 0 && ahorroReal > 0 && (
          <p style={s.ahorroSub}>
            ahorrado este mes: <strong style={{ color: theme.gold }}>${fmt(ahorroReal)}</strong>
          </p>
        )}
      </div>

      {/* ── Bottom sheets ── */}

      {/* Single category / special sheet */}
      {sheetCat && sheetCat !== '__all__' && sheetCat !== '__picker__' && (
        <div style={s.overlay} onClick={closeSheet}>
          <div style={s.sheet} onClick={e => e.stopPropagation()}>
            <div style={s.handle} />
            <h2 style={s.sheetTitle}>
              {sheetCat === '__ingreso__' ? 'ingreso planificado'
                : sheetCat === '__ahorro__' ? 'meta de ahorro mensual'
                : sheetCat}
            </h2>
            {sheetCat === '__ingreso__' && (
              <p style={s.sheetSub}>¿cuánto esperas ganar este mes?</p>
            )}
            {sheetCat === '__ahorro__' && (
              <p style={s.sheetSub}>¿cuánto quieres ahorrar este mes?</p>
            )}
            {sheetCat !== '__ingreso__' && sheetCat !== '__ahorro__' && (porCategoria[sheetCat] || 0) > 0 && (
              <p style={s.sheetCurrentSpend}>
                gastado este mes: <strong>${fmt(porCategoria[sheetCat] || 0)}</strong>
              </p>
            )}
            <div style={s.field}>
              <p style={s.fieldLabel}>
                {sheetCat === '__ingreso__' ? 'ingreso estimado'
                  : sheetCat === '__ahorro__' ? 'meta de ahorro'
                  : 'límite mensual'}
              </p>
              <input
                style={s.input}
                type="text"
                inputMode="decimal"
                placeholder="ej: 3000"
                value={inputLimite}
                autoFocus
                onChange={e => {
                  const v = e.target.value.replace(/[^0-9.]/g, '')
                  if ((v.match(/\./g) || []).length <= 1) setInputLimite(v)
                }}
              />
            </div>
            <button
              style={{
                ...s.sheetBtn,
                opacity: !inputLimite || parseFloat(inputLimite) <= 0 ? 0.5 : 1,
              }}
              disabled={!inputLimite || parseFloat(inputLimite) <= 0 || saving}
              onClick={async () => {
                await guardarLimite(sheetCat, parseFloat(inputLimite))
                closeSheet()
              }}
            >
              {saving ? 'guardando...' : 'guardar ✦'}
            </button>
            {limites[sheetCat] != null && (
              <button
                style={{ ...s.sheetBtnSecondary }}
                disabled={saving}
                onClick={async () => {
                  await eliminarLimite(sheetCat)
                  closeSheet()
                }}
              >
                quitar límite
              </button>
            )}
          </div>
        </div>
      )}

      {/* All categories sheet */}
      {sheetCat === '__all__' && (
        <div style={s.overlay} onClick={closeSheet}>
          <div style={s.sheet} onClick={e => e.stopPropagation()}>
            <div style={s.handle} />
            <h2 style={s.sheetTitle}>editar <em style={{ color: theme.accent, fontStyle: 'italic' }}>presupuestos</em></h2>
            <p style={s.sheetSub}>déjalo vacío para no tener límite en esa categoría</p>
            {CATEGORIAS_GASTO.map(({ label }) => (
              <div key={label} style={s.sheetRow}>
                <span style={s.sheetRowLabel}>{label}</span>
                <input
                  style={s.sheetRowInput}
                  type="text"
                  inputMode="decimal"
                  placeholder="sin límite"
                  value={tempLimites[label] != null ? String(tempLimites[label]) : ''}
                  onChange={e => {
                    const v = e.target.value.replace(/[^0-9.]/g, '')
                    setTempLimites(prev => ({ ...prev, [label]: v }))
                  }}
                />
              </div>
            ))}
            <button
              style={s.sheetBtn}
              disabled={saving}
              onClick={async () => {
                const saves   = Object.entries(tempLimites)
                  .filter(([, v]) => v !== '' && parseFloat(v) > 0)
                  .map(([cat, v]) => guardarLimite(cat, parseFloat(v)))
                const deletes = Object.entries(tempLimites)
                  .filter(([cat, v]) => (v === '' || parseFloat(v) <= 0) && limites[cat] != null)
                  .map(([cat]) => eliminarLimite(cat))
                await Promise.all([...saves, ...deletes])
                closeSheet()
              }}
            >
              {saving ? 'guardando...' : 'guardar todos ✦'}
            </button>
          </div>
        </div>
      )}

      {/* Category picker sheet */}
      {sheetCat === '__picker__' && (
        <div style={s.overlay} onClick={closeSheet}>
          <div style={s.sheet} onClick={e => e.stopPropagation()}>
            <div style={s.handle} />
            <h2 style={s.sheetTitle}>¿cuál <em style={{ color: theme.accent, fontStyle: 'italic' }}>categoría</em>?</h2>
            {CATEGORIAS_GASTO.map(({ label }) => (
              <button
                key={label}
                style={s.pickerRow}
                onClick={() => openCatSheet(label)}
              >
                <span>{label}</span>
                <span style={{ color: theme.accent, fontSize: 18 }}>›</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const makeStyles = (t) => ({
  container:     { maxWidth: 560, margin: '0 auto', padding: '32px 22px 120px', background: t.cream, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" },
  loading:       { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: t.inkLight, background: t.cream },

  eyebrow:       { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 15, color: t.inkLight, margin: '0 0 4px', letterSpacing: 0.2 },
  mesNav:        { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  titulo:        { fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 300, color: t.ink, letterSpacing: 0.3, flex: 1, margin: 0, textTransform: 'capitalize' },
  navBtn:        { background: 'none', border: 'none', fontSize: 28, color: t.inkMid, cursor: 'pointer', padding: '4px 8px', lineHeight: 1 },

  inheritedBanner: { display: 'flex', alignItems: 'center', gap: 8, background: t.goldPale + '55', border: `1px solid ${t.gold}44`, borderRadius: 12, padding: '8px 12px', marginBottom: 18 },
  inheritedIc:     { fontSize: 14, flexShrink: 0 },
  inheritedText:   { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 12, color: t.inkMid, margin: 0, lineHeight: 1.5 },

  secHeader:     { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '0 0 10px' },
  secTitulo:     { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 400, color: t.ink, margin: 0, letterSpacing: 0.3 },
  editarBtn:     { background: 'none', border: 'none', fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 15, color: t.accent, cursor: 'pointer', padding: 0 },
  editChip:      { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 13, color: t.accent, flexShrink: 0 },

  // Ingresos
  ingresoCard:   { background: t.paper, border: `1.5px solid ${t.sage}44`, borderRadius: 20, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10, cursor: 'pointer' },
  ingresoLeft:   { flex: 1, minWidth: 0 },
  ingresoEye:    { fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkLight, fontWeight: 500, margin: '0 0 2px' },
  ingresoBig:    { fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 600, color: t.ink, margin: '0 0 2px', lineHeight: 1 },
  ingresoSub:    { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 12, color: t.inkLight, margin: 0, lineHeight: 1.4 },
  ingresoEdit:   { flexShrink: 0 },

  // Sobre de distribución
  sobreCard:     { background: t.paper, border: `1.5px solid ${t.cardBorder}`, borderRadius: 20, padding: '14px 16px', marginBottom: 18 },
  sobreTitle:    { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 13, color: t.inkLight, margin: '0 0 10px' },
  sobreRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 7, marginBottom: 7 },
  sobreLabel:    { fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: t.inkMid },
  sobreVal:      { fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, color: t.ink },
  sobreDivider:  { height: 1, background: t.txBorder, margin: '4px 0 8px' },

  // Hero donut
  heroCard:      { background: t.paper, border: `1.5px solid ${t.cardBorder}`, borderRadius: 24, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, boxShadow: `0 8px 24px ${t.ink}08` },
  donutWrap:     { position: 'relative', flexShrink: 0, width: 150, height: 150 },
  donutCenter:   { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  donutPct:      { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 600, color: t.ink, lineHeight: 1 },
  donutCenterLabel: { fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: t.inkLight, marginTop: 2 },
  donutSide:     { flex: 1, minWidth: 0 },
  donutEye:      { fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkLight, fontWeight: 500, margin: '0 0 2px' },
  donutBig:      { fontFamily: "'Cormorant Garamond', serif", fontSize: 30, fontWeight: 600, color: t.ink, margin: '0 0 2px', lineHeight: 1 },
  donutSm:       { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 13, color: t.inkLight, margin: '0 0 10px', lineHeight: 1.4 },
  chipsRow:      { display: 'flex', flexWrap: 'wrap', gap: 5 },
  chip:          { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 20, border: '1.5px solid', fontSize: 10, fontWeight: 500 },
  chipDot:       { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },

  // Insights
  insightCard:   { background: t.paper, border: `1.5px solid ${t.cardBorder}`, borderRadius: 16, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 },
  insightIc:     { width: 32, height: 32, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 16 },
  insightText:   { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 14, color: t.inkMid, lineHeight: 1.6, margin: 0 },

  // Categorías
  catCard:       { background: t.paper, border: `1.5px solid ${t.cardBorder}`, borderRadius: 20, padding: '13px 14px', display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 9, cursor: 'pointer', transition: 'opacity 0.15s' },
  catCardWarn:   { borderColor: t.terra + '66', background: t.blush + '14' },
  catIconWrap:   { width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  catInfo:       { flex: 1, minWidth: 0 },
  catTop:        { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 },
  catNombre:     { fontSize: 14, fontWeight: 500, color: t.ink },
  catMonto:      { fontFamily: "'Cormorant Garamond', serif", fontSize: 16, fontWeight: 600 },
  barTrack:      { height: 6, background: t.blush + '44', borderRadius: 6, overflow: 'hidden', marginBottom: 4 },
  barFill:       { height: '100%', borderRadius: 6, transition: 'width 0.4s ease' },
  catBottom:     { display: 'flex', justifyContent: 'space-between' },
  catPct:        { fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: t.inkLight },
  catLibre:      { fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: t.inkLight },
  sinLimite:     { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 12, color: t.inkLight, margin: 0 },

  addCatBtn:     { width: '100%', padding: 13, borderRadius: 18, border: `2px dashed ${t.accent}55`, background: 'transparent', fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: t.accent, cursor: 'pointer', marginTop: 4 },

  // Ahorro
  ahorroCard:    { background: t.paper, border: `1.5px solid ${t.gold}44`, borderRadius: 20, padding: '14px 16px', cursor: 'pointer', marginBottom: 12 },
  ahorroTop:     { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 },
  ahorroEye:     { fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkLight, fontWeight: 500, margin: '0 0 2px' },
  ahorroBig:     { fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 600, color: t.ink, margin: 0, lineHeight: 1 },
  ahorroSub:     { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 12, color: t.inkLight, margin: '0 0 8px', lineHeight: 1.4 },
  ahorroPct:     { fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: t.inkLight, margin: '4px 0 0', textAlign: 'right' },

  // Sheets
  overlay:       { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 300, display: 'flex', alignItems: 'flex-end' },
  sheet:         { background: t.paper, borderRadius: '28px 28px 0 0', padding: '20px 24px 44px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },
  handle:        { width: 40, height: 4, borderRadius: 2, background: t.blush, margin: '0 auto 18px' },
  sheetTitle:    { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 400, color: t.ink, margin: '0 0 6px' },
  sheetSub:      { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 14, color: t.inkLight, margin: '0 0 16px' },
  sheetCurrentSpend: { fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: t.inkMid, margin: '0 0 12px' },
  field:         { marginBottom: 14 },
  fieldLabel:    { fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: t.inkLight, fontWeight: 500, margin: '0 0 7px' },
  input:         { width: '100%', padding: '13px 14px', borderRadius: 14, border: `1.5px solid ${t.cardBorder}`, background: t.inputBg, fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: t.ink, outline: 'none', boxSizing: 'border-box' },
  sheetBtn:          { width: '100%', padding: 15, borderRadius: 18, border: 'none', background: t.cardGradient, color: 'white', fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: 22, cursor: 'pointer', marginTop: 8, boxShadow: `0 8px 20px ${t.accentDeep}44` },
  sheetBtnSecondary: { width: '100%', padding: 12, borderRadius: 18, border: `1.5px solid ${t.cardBorder}`, background: 'transparent', color: t.inkLight, fontFamily: "'DM Sans', sans-serif", fontSize: 14, cursor: 'pointer', marginTop: 8 },

  sheetRow:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingBottom: 12, marginBottom: 4, borderBottom: `1px solid ${t.txBorder}` },
  sheetRowLabel: { fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: t.ink, flex: 1 },
  sheetRowInput: { width: 100, padding: '7px 10px', borderRadius: 10, border: `1.5px solid ${t.cardBorder}`, background: t.inputBg, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: t.ink, outline: 'none', textAlign: 'right', flexShrink: 0 },

  pickerRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 0', borderBottom: `1px solid ${t.txBorder}`, background: 'none', width: '100%', fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: t.ink, cursor: 'pointer', textAlign: 'left' },
})
